import { S } from "../theme";

function LandingPage({ onGetStarted, viewLegal }) {
  const features = [
    { icon: "📋", title: "Protocol Tracker", desc: "Log every peptide, dose, route and frequency. Visual week view with streak tracking." },
    { icon: "🤖", title: "AI Guidance 24/7", desc: "Ask anything about peptides and get research-backed answers instantly. Knows your stack." },
    { icon: "📊", title: "Biomarker Logging", desc: "Track IGF-1, testosterone, sleep, HRV and more. See trends across your full timeline." },
    { icon: "⚖️", title: "Live FDA Updates", desc: "What changed, in plain language. Caught up, so you don't have to be." },
    { icon: "🔬", title: "Peptide Library", desc: "32+ compound profiles, in language a person actually uses." },
    { icon: "💉", title: "Injection Tracker", desc: "Sites, inventory, reconstitution math — handled, not hunted." },
  ];

  const testimonials = [
    { initials: "📋", name: "Track everything in one place", role: "No more scattered notes", quote: "Log a dose. See your week. Slowly build the streak you weren't expecting.", color: "#2d6a4f" },
    { initials: "🤖", name: "Understand what you're doing", role: "Research-backed, plain English", quote: "Ask a question. Get a plain-language answer. No PhD, no forum spiral.", color: "#8b5cf6" },
    { initials: "📊", name: "See if it's actually working", role: "Biomarkers + subjective tracking", quote: "Track what matters to you. Watch the trend, not the noise.", color: "#e76f3b" },
  ];

  return (
    <div style={{ fontFamily: "'Inter', sans-serif", background: S.bg, minHeight: "100vh" }}>

      {/* NAV */}
      <nav className="tmp-landing-nav" style={{ position: "sticky", top: 0, zIndex: 100, padding: "0 24px", height: 60, display: "flex", alignItems: "center", justifyContent: "space-between", background: "rgba(15,21,18,0.92)", backdropFilter: "blur(16px)", borderBottom: `1px solid ${S.border}` }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: "1.15rem", letterSpacing: "-0.02em", color: S.ink }}>
          Track<span style={{ color: S.accent }}>My</span>Peptide
        </span>
        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={() => onGetStarted("login")} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.85rem", color: S.ink2, background: "transparent", border: `1.5px solid ${S.border}`, padding: "9px 20px", borderRadius: 100, cursor: "pointer" }}>Log In</button>
          <button onClick={() => onGetStarted("signup")} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "0.85rem", color: "#fff", background: S.accent, border: "none", padding: "9px 20px", borderRadius: 100, cursor: "pointer", boxShadow: "0 4px 16px rgba(45,106,79,0.25)" }}>Start 7-Day Free Trial →</button>
        </div>
      </nav>

      {/* HERO — feel-first, cinematic */}
      <section className="tmp-hero" style={{ position: "relative", width: "100%", height: "min(100vh, 760px)", minHeight: 580, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {/* Background video */}
        <video
          autoPlay loop muted playsInline
          src="https://d8j0ntlcm91z4.cloudfront.net/user_3EFNsf0RsuI7VfL75yUaSoEzCwQ/hf_20260530_233108_ddd49bce-d637-49a6-a9b1-be3156f25097.mp4"
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", zIndex: 0 }}
        />
        {/* Warm overlay so text is legible without crushing the feel */}
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(180deg, rgba(13,11,8,0.35) 0%, rgba(13,11,8,0.55) 60%, rgba(13,11,8,0.85) 100%)", zIndex: 1 }} />

        {/* Hero copy — quiet, centered, serif */}
        <div className="tmp-hero-copy" style={{ position: "relative", zIndex: 2, textAlign: "center", maxWidth: 720, padding: "0 32px" }}>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.72rem", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: S.ink2, marginBottom: 36, opacity: 0.85 }}>
            for women managing pcos &amp; glp-1
          </p>
          <h1 className="tmp-hero-h1" style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(2.4rem, 5.5vw, 4.6rem)", fontWeight: 400, letterSpacing: "-0.025em", lineHeight: 1.05, color: S.ink, marginBottom: 28 }}>
            The quiet relief of <em style={{ fontStyle: "italic", fontWeight: 400, color: S.accent }}>finally</em><br />having it handled.
          </h1>
          <p style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(1rem, 1.6vw, 1.18rem)", fontWeight: 300, fontStyle: "italic", color: S.ink2, lineHeight: 1.65, maxWidth: 520, margin: "0 auto 44px", opacity: 0.92 }}>
            Your cycle, your symptoms, your doses — somehow, somewhere, all in one place.
          </p>
          <div className="tmp-hero-cta" style={{ display: "flex", gap: 14, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <button onClick={() => onGetStarted("signup")} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 600, fontSize: "0.95rem", color: "#fff", background: S.accent, border: "none", padding: "15px 32px", borderRadius: 100, cursor: "pointer", boxShadow: "0 8px 32px rgba(126,171,142,0.25)", transition: "all 0.2s", letterSpacing: "0.01em" }}>
              start your free trial
            </button>
            <button onClick={() => onGetStarted("login")} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 500, fontSize: "0.92rem", color: S.ink2, background: "transparent", border: "none", padding: "15px 18px", cursor: "pointer", borderBottom: `1px solid ${S.ink2}66`, borderRadius: 0 }}>
              already have an account
            </button>
          </div>
        </div>

        {/* Subtle scroll hint */}
        <div style={{ position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)", zIndex: 2, color: S.ink2, opacity: 0.5, fontSize: "0.7rem", letterSpacing: "0.2em", textTransform: "uppercase", fontFamily: "'Inter', sans-serif" }}>scroll</div>
      </section>

      {/* DASHBOARD MOCKUP */}
      <section className="tmp-landing-section" style={{ background: S.darkPanel, padding: "80px 48px", borderRadius: 24, border: `1px solid ${S.border}` }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: S.accent, marginBottom: 16 }}>the app</div>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(1.9rem, 4vw, 2.8rem)", fontWeight: 400, color: S.ink, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
              Everything, <em style={{ fontStyle: "italic", fontWeight: 400, color: S.accent }}>somehow</em>, in one place.
            </h2>
          </div>

          {/* Mock dashboard */}
          <div style={{ background: S.bg, borderRadius: 24, overflow: "hidden", boxShadow: "0 40px 80px rgba(0,0,0,0.5)", border: `1px solid ${S.border}` }}>
            <div style={{ display: "flex" }}>
              {/* Mock sidebar */}
              <div className="tmp-mockup-sidebar" style={{ width: 180, background: S.darkPanel, padding: "20px 12px", flexShrink: 0 }}>
                <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: "0.85rem", color: "#fff", padding: "0 10px", marginBottom: 20 }}>Track<span style={{ color: S.accent }}>My</span>Peptide</div>
                {[["🏠","Dashboard",true],["📋","Protocol",false],["✅","Track Today",false],["📊","Biomarkers",false],["🔬","Library",false]].map(([icon, label, active]) => (
                  <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, padding: "9px 10px", borderRadius: 10, marginBottom: 3, background: active ? "rgba(45,106,79,0.25)" : "transparent", borderLeft: active ? `3px solid ${S.accent}` : "3px solid transparent", color: active ? "#fff" : "rgba(255,255,255,0.4)", fontSize: "0.75rem", fontWeight: 500 }}>
                    <span>{icon}</span>{label}
                  </div>
                ))}
              </div>
              {/* Mock content */}
              <div style={{ flex: 1, padding: 24 }}>
                <div style={{ fontSize: "0.7rem", color: S.muted, marginBottom: 4 }}>Good morning,</div>
                <div style={{ fontFamily: "'Fraunces', serif", fontSize: "1.4rem", fontWeight: 500, color: S.ink, marginBottom: 20 }}>Here's your week 🌿</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 10, marginBottom: 16 }}>
                  {[["3", "Active peptides"],["86%","Adherence"],["18","Day streak"]].map(([v,l]) => (
                    <div key={l} style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 12, padding: "14px 16px" }}>
                      <div style={{ fontFamily: "'Fraunces', serif", fontSize: "1.6rem", fontWeight: 500, color: S.ink }}>{v}</div>
                      <div style={{ fontSize: "0.65rem", color: S.muted, textTransform: "uppercase", letterSpacing: "0.12em", fontWeight: 500 }}>{l}</div>
                    </div>
                  ))}
                </div>
                {[["BPC-157","250mcg · SubQ","#2d6a4f",true],["TB-500","2.5mg · SubQ","#6b4fbb",true],["Ipamorelin","200mcg · Bedtime","#e76f3b",false]].map(([name,dose,color,done]) => (
                  <div key={name} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 14px", background: done ? S.accentLight : S.white, border: `1px solid ${done ? S.accent+"44" : S.border}`, borderRadius: 12, marginBottom: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: "50%", background: color }} />
                    <div style={{ fontWeight: 600, fontSize: "0.9rem", flex: 1, color: S.accent2 }}>{name}</div>
                    <div style={{ fontSize: "0.72rem", color: S.muted }}>{dose}</div>
                    <div style={{ width: 24, height: 24, borderRadius: 7, background: done ? S.accent : "transparent", border: `2px solid ${done ? S.accent : S.border}`, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: "0.7rem" }}>{done ? "✓" : ""}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="tmp-landing-section" style={{ maxWidth: 1100, margin: "0 auto", padding: "100px 48px" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: S.accent, marginBottom: 16 }}>inside</div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(2rem, 4vw, 3rem)", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em" }}>
            Everything the peptide<br />community was <em style={{ fontStyle: "italic", fontWeight: 300, color: S.muted }}>missing.</em>
          </h2>
        </div>
        <div className="tmp-landing-features" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
          {features.map(f => (
            <div key={f.title} style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 20, padding: "32px 28px" }}>
              <div style={{ fontSize: "2rem", marginBottom: 16 }}>{f.icon}</div>
              <div style={{ fontFamily: "'Fraunces', serif", fontWeight: 700, fontSize: "1.1rem", color: S.ink, marginBottom: 10 }}>{f.title}</div>
              <div style={{ fontSize: "0.82rem", color: S.muted, lineHeight: 1.7 }}>{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="tmp-landing-section" style={{ background: S.surface2, padding: "80px 48px" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 56 }}>
            <div style={{ fontSize: "0.72rem", fontWeight: 700, letterSpacing: "0.15em", textTransform: "uppercase", color: S.accent, marginBottom: 12 }}>// What you get</div>
            <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(2rem, 4vw, 2.8rem)", fontWeight: 900, color: S.ink, letterSpacing: "-0.03em" }}>Built for how you actually live.</h2>
          </div>
          <div className="tmp-landing-testimonials" style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 20 }}>
            {testimonials.map(t => (
              <div key={t.name} style={{ background: S.white, border: `1px solid ${S.border}`, borderRadius: 20, padding: 32 }}>
                <div style={{ fontSize: "2rem", color: S.border, fontFamily: "'Fraunces', serif", lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ fontSize: "0.88rem", color: S.ink2, lineHeight: 1.75, marginBottom: 24, fontStyle: "italic" }}>"{t.quote}"</p>
                <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${t.color}, ${t.color}99)`, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'Fraunces', serif", fontWeight: 900, color: "#fff", fontSize: "0.85rem" }}>{t.initials}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: "0.85rem", color: S.ink }}>{t.name}</div>
                    <div style={{ fontSize: "0.72rem", color: S.muted }}>{t.role}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA BANNER */}
      <section className="tmp-landing-section" style={{ background: "linear-gradient(135deg, #16271f, #0a0e0c)", padding: "100px 48px", textAlign: "center", border: `1px solid ${S.border}` }}>
        <div style={{ maxWidth: 640, margin: "0 auto" }}>
          <div style={{ fontFamily: "'Inter', sans-serif", fontSize: "0.7rem", fontWeight: 500, letterSpacing: "0.22em", textTransform: "uppercase", color: S.accent, marginBottom: 22 }}>try it free for 7 days</div>
          <h2 style={{ fontFamily: "'Fraunces', serif", fontSize: "clamp(2.5rem, 5vw, 4rem)", fontWeight: 900, color: S.ink, letterSpacing: "-0.04em", lineHeight: 0.95, marginBottom: 24 }}>
            The week it just <em style={{ fontStyle: "italic", fontWeight: 400, color: S.accent }}>becomes</em><br />routine.
          </h2>
          <p style={{ fontSize: "0.95rem", color: S.muted, lineHeight: 1.7, marginBottom: 40 }}>
            You show up, you log, you keep going. One day you'll notice you've been showing up — and that the thing you dreaded just became part of your morning.
          </p>
          <button onClick={() => onGetStarted("signup")} style={{ fontFamily: "'Inter', sans-serif", fontWeight: 700, fontSize: "1rem", color: "#fff", background: S.accent, border: "none", padding: "18px 40px", borderRadius: 100, cursor: "pointer", boxShadow: "0 8px 32px rgba(61,155,114,0.3)" }}>
            start your free trial
          </button>
          <p style={{ fontSize: "0.72rem", color: S.muted, marginTop: 16 }}>7 days free, then $29/mo or $290/yr. Cancel anytime.</p>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="tmp-landing-footer" style={{ background: S.darkPanel, borderTop: "1px solid rgba(255,255,255,0.06)", padding: "28px 48px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <span style={{ fontFamily: "'Fraunces', serif", fontWeight: 900, fontSize: "1rem", color: "#fff" }}>Track<span style={{ color: S.accent }}>My</span>Peptide.com</span>
        <span style={{ fontSize: "0.68rem", color: "rgba(255,255,255,0.25)", maxWidth: 500, textAlign: "center", lineHeight: 1.6 }}>Educational. Yours. Not medical advice — always consult your physician.</span>
        <div style={{ display: "flex", gap: 20 }}>
          {[["Privacy","privacy"],["Terms","terms"],["Disclaimer","disclaimer"]].map(([label, key]) => (
            <button key={key} onClick={() => viewLegal(key)} style={{ fontSize: "0.72rem", color: "rgba(255,255,255,0.3)", textDecoration: "none", background: "none", border: "none", cursor: "pointer", fontFamily: "'Inter', sans-serif", padding: 0 }}>{label}</button>
          ))}
        </div>
      </footer>

      <style>{`
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.3} }

        @media (max-width: 900px) {
          /* Landing hero stacks */
          .tmp-landing-hero { grid-template-columns: 1fr !important; gap: 40px !important; padding: 80px 20px 60px !important; }

          /* Track calendar grid stacks */
          .tmp-track-grid { grid-template-columns: 1fr !important; display: flex !important; flex-direction: column !important; gap: 20px !important; }
          .tmp-track-grid > * { width: 100% !important; max-width: 100% !important; min-width: 0 !important; }

          /* Dashboard top grids stack */
          .tmp-dash-top { grid-template-columns: 1fr !important; }

          /* Reduce page padding on mobile so content fits */
          .tmp-page-pad { padding: 20px 16px !important; }

          /* Section padding */
          .tmp-landing-section { padding: 60px 20px !important; }

          /* Smaller hero headline */
          .tmp-landing-h1 { font-size: 2.4rem !important; }

          /* Stats: 2x2 grid on mobile instead of 4 cols */
          .tmp-landing-stats { flex-wrap: wrap !important; }
          .tmp-landing-stats > div { flex: 0 0 50% !important; padding: 16px !important; border-right: none !important; }

          /* Feature grid stacks */
          .tmp-landing-features { grid-template-columns: 1fr !important; gap: 14px !important; }

          /* Testimonials stack */
          .tmp-landing-testimonials { grid-template-columns: 1fr !important; gap: 14px !important; }

          /* Footer wraps */
          .tmp-landing-footer { flex-direction: column !important; gap: 16px !important; text-align: center !important; padding: 24px 20px !important; }

          /* Mockup sidebar hides on phone */
          .tmp-mockup-sidebar { display: none !important; }

          /* Nav buttons get smaller */
          .tmp-landing-nav button { padding: 7px 14px !important; font-size: 0.78rem !important; }

          /* Hero CTA buttons stack and go full-width */
          .tmp-hero-cta { flex-direction: column !important; gap: 12px !important; }
          .tmp-hero-cta button { width: 100% !important; max-width: 320px !important; }
        }
        @media (max-width: 480px) {
          .tmp-landing-h1 { font-size: 1.9rem !important; }
          .tmp-landing-section { padding: 48px 18px !important; }
          .tmp-landing-stats > div .stat-num,
          .tmp-landing-stats > div div:first-child { font-size: 1.5rem !important; }
        }
      `}</style>
    </div>
  );
}

// ─── AUTH SCREEN ─────────────────────────────────────────────

export { LandingPage };
